import React from "react";
import { motion } from "framer-motion";
import {
  ShieldCheckIcon,
  GlobeAltIcon,
  LockClosedIcon,
  DocumentTextIcon,
  UserGroupIcon,
  CpuChipIcon,
  KeyIcon,
  EyeSlashIcon,
  FingerPrintIcon
} from "@heroicons/react/24/outline";

const GlobalPrivacyPage = () => {
  const regulations = [
    {
      icon: GlobeAltIcon,
      name: "GDPR",
      description: "A Regulation governing data protection and privacy for individuals in the EU and EEA.",
      gradient: "from-brand to-brand-dark",
      region: "Europe"
    },
    {
      icon: ShieldCheckIcon,
      name: "CCPA",
      description: "Protects consumer data privacy rights and governs personal information practices in California.",
      gradient: "from-green-500 to-emerald-500",
      region: "California, USA"
    },
    {
      icon: LockClosedIcon,
      name: "PDPA",
      description: "Governs personal data protection and ensures privacy rights across various jurisdictions.",
      gradient: "from-purple-500 to-violet-500",
      region: "Asia-Pacific"
    },
    {
      icon: DocumentTextIcon,
      name: "PIPEDA",
      description: "A federal law governing how organizations collect and disclose personal information in Canada.",
      gradient: "from-red-500 to-pink-500",
      region: "Canada"
    },
    {
      icon: UserGroupIcon,
      name: "HIPAA",
      description: "Ensures the protection of health information by setting standards for secure handling, storage, and transmission of patient data.",
      gradient: "from-orange-500 to-yellow-500",
      region: "USA (Healthcare)"
    },
    {
      icon: FingerPrintIcon,
      name: "DPDPA",
      description: "Regulates the collection, use, and protection of personal data in digital platforms, ensuring user privacy and compliance with privacy rights.",
      gradient: "from-teal-500 to-cyan-500",
      region: "India"
    }
  ];

  const benefits = [
    "AI-driven privacy solutions protect data across global jurisdictions",
    "Expert insights enable adaptation to regulatory changes worldwide",
    "Innovative tech ensures global data security compliance"
  ];

  const features = [
    {
      icon: CpuChipIcon,
      title: "AI-Powered Privacy Protection",
      description: "Advanced artificial intelligence monitors and protects personal data across all global jurisdictions automatically."
    },
    {
      icon: GlobeAltIcon,
      title: "Cross-Border Compliance",
      description: "Seamlessly manage data transfers and privacy compliance across multiple international regulations."
    },
    {
      icon: EyeSlashIcon,
      title: "Enhanced Security Controls",
      description: "Implement robust security measures and privacy controls that adapt to evolving regulatory requirements."
    },
    {
      icon: KeyIcon,
      title: "Automated Compliance Reporting",
      description: "Generate comprehensive compliance reports automatically for all supported privacy frameworks and regulations."
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative px-6 pt-12 pb-20 overflow-hidden">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <div className="inline-flex items-center px-4 py-2 bg-white/10 rounded-full border border-white/20 mb-6">
              <GlobeAltIcon className="w-5 h-5 text-cyan-400 mr-2" />
              <span className="text-sm font-medium text-cyan-200">Protecting Privacy Worldwide</span>
            </div>
            
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
              Global
              <span className="bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent block">
                Privacy
              </span>
            </h1>
            
            <p className="text-xl md:text-2xl text-gray-300 max-w-4xl mx-auto mb-12 leading-relaxed">
              Achieve global privacy excellence with AI-driven solutions and expert insights. 
              Tanikkai's cutting-edge technology guarantees robust data protection and seamless 
              compliance with evolving international regulations, safeguarding your information across borders.
            </p>

            <div className="flex items-center justify-center mb-12">
              <img 
                src="https://images.pexels.com/photos/7564228/pexels-photo-7564228.jpeg" 
                alt="Global Privacy Protection Network and Compliance"
                className="max-w-2xl w-full h-auto rounded-2xl shadow-2xl border border-white/20"
              />
            </div>

            {/* Benefits */}
            <div className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm rounded-3xl p-8 border border-white/20">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {benefits.map((benefit, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.2 }}
                    className="flex items-center space-x-3"
                  >
                    <div className="w-2 h-2 bg-cyan-400 rounded-full flex-shrink-0"></div>
                    <p className="text-gray-300 text-sm">{benefit}</p>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>
        </div>

        {/* Background Elements */}
        <div className="absolute inset-0 -z-10">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-cyan-500/20 rounded-full blur-3xl"></div>
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-blue-500/20 rounded-full blur-3xl"></div>
        </div>
      </section>

      {/* Features Section */}
      <section className="relative px-6 py-20 bg-gradient-to-r from-slate-900/50 to-blue-900/30 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">AI-Driven Privacy Solutions</h2>
            <p className="text-xl text-gray-300 max-w-4xl mx-auto">
              Tanikkai's AI-driven solutions uphold global data privacy, manage cross-border transfers, 
              enhance security, ensure compliance, and foster growth.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/20 hover:border-white/40 transition-all duration-300"
              >
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-gradient-to-r from-cyan-500 to-blue-600 rounded-xl flex items-center justify-center mr-4">
                    <feature.icon className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-white">{feature.title}</h3>
                </div>
                <p className="text-gray-300 leading-relaxed">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Regulations Grid */}
      <section className="relative px-6 py-20">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">Regulations and Laws</h2>
            <p className="text-xl text-gray-300 max-w-4xl mx-auto">
              Comprehensive support for major global privacy regulations ensuring your organization 
              stays compliant across all jurisdictions
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {regulations.map((regulation, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ scale: 1.02, y: -5 }}
                className="group bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/20 hover:border-white/40 transition-all duration-300"
              >
                <div className="flex items-center mb-6">
                  <div className={`w-14 h-14 bg-gradient-to-r ${regulation.gradient} rounded-xl flex items-center justify-center mr-4 group-hover:scale-110 transition-transform`}>
                    <regulation.icon className="w-7 h-7 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-white group-hover:text-cyan-300 transition-colors">
                      {regulation.name}
                    </h3>
                    <p className="text-cyan-400 text-sm font-medium">{regulation.region}</p>
                  </div>
                </div>
                
                <p className="text-gray-300 leading-relaxed text-sm mb-4">
                  {regulation.description}
                </p>

                {/* AI-Powered Badge */}
                <div className="flex items-center justify-between">
                  <span className="inline-flex items-center px-3 py-1 bg-gradient-to-r from-green-500/20 to-emerald-500/20 rounded-full border border-green-400/30">
                    <CpuChipIcon className="w-4 h-4 text-green-400 mr-1" />
                    <span className="text-xs font-medium text-green-300">AI-Monitored</span>
                  </span>
                  <span className="text-xs text-blue-400 font-medium">Auto-Compliance</span>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative px-6 py-20 bg-gradient-to-r from-slate-900/50 to-blue-900/30 backdrop-blur-sm">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm rounded-3xl p-12 border border-white/20"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Secure Your Global Privacy Compliance
            </h2>
            <p className="text-xl text-gray-300 mb-8">
              Protect your organization's data across all international jurisdictions with AI-powered privacy management
            </p>
            
            <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-6">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="bg-gradient-to-r from-cyan-500 to-blue-600 text-white px-8 py-4 rounded-2xl font-bold text-lg hover:from-cyan-600 hover:to-blue-700 transition-all shadow-2xl border-2 border-white/20"
              >
                Get Privacy Assessment
              </motion.button>
              
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="bg-white/10 backdrop-blur-sm text-white px-8 py-4 rounded-2xl font-bold text-lg hover:bg-white/20 transition-all border-2 border-white/30"
              >
                Learn More
              </motion.button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default GlobalPrivacyPage;